import { useQuery } from '@tanstack/react-query';
import type { PharmacyItemView } from '@wodi/shared';
import { SearchPicker, Tag, type SearchPickerStatus } from '@wodi/ui';
import { useEffect, useState, type Ref } from 'react';

import { ApiError } from '../api/client.ts';
import { searchPharmacyItems } from '../api/pharmacy.ts';
import { money } from '../billing/format.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatQuantity } from '../stock/format.ts';
import styles from './pharmacy.module.css';

const MIN_CHARS = 2;

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, ms]);
  return debounced;
}

/**
 * Type-to-search for a medicine at a pharmacy store (DESIGN.md §7): 44px rows with name,
 * stock, price and a "Prescription only" tag. A scanned barcode is an exact match, so Enter
 * adds it at once. Rows keep the server's order, which never depends on stock.
 */
export function PharmacyItemPicker({
  storeId,
  label = 'Find a medicine',
  onSelect,
  inputRef,
  autoFocus,
}: {
  storeId: string;
  label?: string;
  onSelect: (item: PharmacyItemView) => void;
  inputRef?: Ref<HTMLInputElement>;
  autoFocus?: boolean;
}) {
  const online = useOnline();
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const debounced = useDebounced(trimmed, 200);
  const search = useQuery({
    queryKey: ['pharmacy-item-search', storeId, debounced],
    queryFn: () => searchPharmacyItems(storeId, debounced),
    enabled: online && debounced.length >= MIN_CHARS,
    staleTime: 10_000,
    retry: false,
  });
  let status: SearchPickerStatus;
  if (trimmed.length < MIN_CHARS) status = 'idle';
  else if (!online) status = 'offline';
  else if (debounced !== trimmed || search.isFetching || search.isPending) status = 'searching';
  else if (search.isError) status = 'error';
  else status = 'ready';

  return (
    <SearchPicker
      label={label}
      description="Type a name or code, or scan a barcode."
      placeholder="Name, code or barcode"
      inputValue={text}
      onInputChange={setText}
      status={status}
      items={search.data ?? []}
      itemsQuery={debounced}
      getKey={(item) => item.id}
      getTextValue={(item) => `${item.name} ${item.code}`}
      renderItem={(item) => (
        <span className={styles.pickerRow}>
          <span className={styles.pickerName}>{item.name}</span>
          <span className="wodi-num">
            {item.cashPrice === null ? 'No price' : money(item.cashPrice)}
          </span>
          <span className={styles.pickerMeta}>
            <span className="wodi-id">{item.code}</span>
            <span>{`${formatQuantity(item.usable, item.baseUnitDisplay)} in stock`}</span>
            {item.supplyClass === 'prescription_only' ? (
              <Tag state="pending">Prescription only</Tag>
            ) : null}
            {item.isControlled ? <Tag state="pending">Controlled</Tag> : null}
          </span>
        </span>
      )}
      isExactMatch={(item, query) =>
        item.scanned !== null || item.code.toUpperCase() === query.toUpperCase()
      }
      onSelect={(item) => {
        setText('');
        onSelect(item);
      }}
      promptMessage="Type at least two letters of the medicine, or scan its barcode."
      emptyMessage="No medicine matches. Check the spelling, or ask for the item to be added to the catalogue."
      {...(search.error === null
        ? {}
        : {
            errorMessage:
              search.error instanceof ApiError
                ? search.error.message
                : 'Search did not work. Try again.',
          })}
      onRetry={() => void search.refetch()}
      offlineMessage="Searching medicines needs a connection to Wodi."
      countMessage={(n) => `${String(n)} ${n === 1 ? 'medicine' : 'medicines'} found`}
      {...(inputRef === undefined ? {} : { inputRef })}
      {...(autoFocus === undefined ? {} : { autoFocus })}
    />
  );
}
